import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Spinner, Toast, ToastTitle, useToastController } from '@fluentui/react-components';
import {
  ArrowDownload20Regular,
  Dismiss20Regular,
  Print20Regular,
} from '@fluentui/react-icons';
import {
  getProcedimiento,
  type ProcedimientoConstructivo,
  type ProcedimientoVariante,
} from '../api/procedimiento.js';
import { GLOBAL_TOASTER_ID } from './ThemedProvider.js';

/**
 * Panel ancho (slide-over) con el PROCEDIMIENTO CONSTRUCTIVO de un concepto de
 * APU-IA (sintético): documento imprimible para el residente. Se renderiza el
 * HTML tal cual se imprime (WYSIWYG carta) dentro de un <iframe> aislado —el
 * documento trae su propio CSS de impresión—, con botones Imprimir y Descargar.
 *
 * Estado con chip "Generando…/Listo" y polling del GET hasta 'completed'. Si el
 * APU cambió, mientras se regenera se muestra el documento anterior (stale).
 *
 * NADA de nombres de modelo/tokens/IA acá: el documento es cara al cliente.
 */

const POLL_MS = 2500;

interface Props {
  conceptId: string;
  /** Variante de croquis a mostrar: 'plantilla' (A) | 'modelo' (B). */
  variante: ProcedimientoVariante;
  /** Clave del concepto (para título y nombre de archivo). */
  clave: string | null;
  /** Descripción del concepto (subtítulo). */
  descripcion: string | null;
  onClose: () => void;
  /** De dónde traer el documento. Default: el de cotización (`getProcedimiento` por conceptId).
   *  /planos pasa el suyo (por renglón de plano) — el documento es EL MISMO. */
  fetcher?: (variante: ProcedimientoVariante) => Promise<ProcedimientoConstructivo>;
}

function nombreArchivo(clave: string | null): string {
  const base = `Procedimiento constructivo${clave ? ` - ${clave}` : ''}`;
  // Limpia caracteres inválidos de nombre de archivo.
  return `${base.replace(/["\\/:*?<>|]/g, ' ').replace(/\s+/g, ' ').trim()}.html`;
}

export function ProcedimientoDrawer({ conceptId, variante, clave, descripcion, onClose, fetcher }: Props) {
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);
  const [data, setData] = useState<ProcedimientoConstructivo | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Cargar + hacer polling mientras esté 'running'. Un solo timer vivo por
  // montaje; `alive` evita setState tras desmontar.
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const r = fetcher ? await fetcher(variante) : await getProcedimiento(conceptId, variante);
        if (!alive) return;
        setData(r);
        setErrorMsg(null);
        if (r.status === 'running') {
          timer = setTimeout(() => void tick(), POLL_MS);
        }
      } catch (e) {
        if (!alive) return;
        setErrorMsg((e as Error).message || 'No se pudo cargar el procedimiento.');
      }
    };
    void tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [conceptId, variante]);

  // Cerrar con Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const html = data?.html ?? null;
  const listo = data?.status === 'completed' && !data.stale;
  const generando = data?.status === 'running' || (!data && !errorMsg);

  const imprimir = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    try {
      win.focus();
      win.print();
    } catch {
      dispatchToast(
        <Toast><ToastTitle>No se pudo abrir la impresión</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    }
  }, [dispatchToast]);

  const descargar = useCallback(() => {
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = nombreArchivo(clave);
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  }, [html, clave]);

  return (
    <>
      {/* Scrim */}
      <div
        onClick={onClose}
        aria-hidden
        style={{
          position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.45)', zIndex: 1000,
        }}
      />
      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Procedimiento constructivo"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1001,
          width: 'min(1040px, 96vw)', maxWidth: '96vw',
          background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-subtle)',
          boxShadow: '-16px 0 40px -18px rgba(16,24,40,0.4)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px',
            borderBottom: '1px solid var(--border-subtle)', flexShrink: 0,
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                Procedimiento constructivo
              </span>
              {listo && <Badge appearance="tint" color="success">Listo</Badge>}
              {data?.status === 'running' && (
                <Badge appearance="tint" color="informative">
                  {data.stale ? 'Actualizando…' : 'Generando…'}
                </Badge>
              )}
              {data?.status === 'failed' && <Badge appearance="tint" color="danger">No se pudo generar</Badge>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {clave ? `${clave} · ` : ''}{descripcion ?? ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <Button
              appearance="secondary"
              size="small"
              icon={<Print20Regular />}
              onClick={imprimir}
              disabled={!html}
            >
              Imprimir
            </Button>
            <Button
              appearance="secondary"
              size="small"
              icon={<ArrowDownload20Regular />}
              onClick={descargar}
              disabled={!html}
            >
              Descargar
            </Button>
            <Button appearance="subtle" size="small" icon={<Dismiss20Regular />} aria-label="Cerrar" onClick={onClose} />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, position: 'relative', background: 'var(--bg-canvas)' }}>
          {html ? (
            <>
              <iframe
                ref={iframeRef}
                title="Procedimiento constructivo"
                srcDoc={html}
                style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              />
              {/* Overlay sutil mientras se regenera una versión nueva sobre la vieja */}
              {data?.status === 'running' && data.stale && (
                <div style={{
                  position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                  borderRadius: 999, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                  boxShadow: '0 2px 12px rgba(16,24,40,0.18)', fontSize: 12, color: 'var(--text-secondary)',
                }}>
                  <Spinner size="extra-tiny" /> Actualizando el procedimiento…
                </div>
              )}
            </>
          ) : (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center',
            }}>
              {errorMsg ? (
                <>
                  <span style={{ fontSize: 13, color: 'var(--bad)', maxWidth: 420, lineHeight: 1.5 }}>{errorMsg}</span>
                  <Button appearance="secondary" size="small" onClick={onClose}>Cerrar</Button>
                </>
              ) : data?.status === 'failed' ? (
                <>
                  <span style={{ fontSize: 13, color: 'var(--bad)', maxWidth: 420, lineHeight: 1.5 }}>
                    {data.error || 'No se pudo generar el procedimiento. Vuelve a intentarlo más tarde.'}
                  </span>
                  <Button appearance="secondary" size="small" onClick={onClose}>Cerrar</Button>
                </>
              ) : data?.status === 'idle' && data.error ? (
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 420, lineHeight: 1.5 }}>{data.error}</span>
              ) : (
                <>
                  <Spinner size="small" />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    Preparando el procedimiento constructivo…
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)', maxWidth: 360, lineHeight: 1.5 }}>
                    Esto puede tardar unos segundos la primera vez. Puedes cerrar y volver cuando esté listo.
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
